import { createAsyncThunk } from '@reduxjs/toolkit';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import { recordingDayRange } from '../../utils/cameraRecording';
import { toCameraApiError } from '../services/cameraApiRequest';
import { deleteCameraClip } from '../services/deleteCameraClip.service';
import { deleteCameraStill } from '../services/deleteCameraStill.service';
import { getCameraClipUrl } from '../services/getCameraClipUrl.service';
import { getCameraClips } from '../services/getCameraClips.service';
import { getCameraStills } from '../services/getCameraStills.service';
import { patchCameraClip } from '../services/patchCameraClip.service';

/** A camera's motion clips of a local day (YYYY-MM-DD), newest first. */
export const loadCameraClips = createAsyncThunk(
  'cameraRecordings/loadClips',
  async ({ projectId, cameraId, day }, { getState, rejectWithValue }) => {
    try {
      const activeEmployerInfo = selectUserActiveEmployerInfo(getState());
      const { from, to } = recordingDayRange(day);
      const { clips } = await getCameraClips({
        projectId,
        cameraId,
        from,
        to,
        activeEmployerInfo,
      });
      return clips || [];
    } catch (error) {
      return rejectWithValue(toCameraApiError(error));
    }
  }
);

/** A camera's stills of a day (YYYY-MM-DD), oldest first. */
export const loadCameraStills = createAsyncThunk(
  'cameraRecordings/loadStills',
  async ({ projectId, cameraId, day }, { getState, rejectWithValue }) => {
    try {
      const activeEmployerInfo = selectUserActiveEmployerInfo(getState());
      const { stills } = await getCameraStills({
        projectId,
        cameraId,
        date: day,
        activeEmployerInfo,
      });
      return stills || [];
    } catch (error) {
      return rejectWithValue(toCameraApiError(error));
    }
  }
);

/** Marks a clip to keep or not; resolves to the API's clip. */
export const setCameraClipKeep = createAsyncThunk(
  'cameraRecordings/setClipKeep',
  async (
    { projectId, cameraId, clipId, keep },
    { getState, rejectWithValue }
  ) => {
    try {
      const activeEmployerInfo = selectUserActiveEmployerInfo(getState());
      const { clip } = await patchCameraClip({
        projectId,
        cameraId,
        clipId,
        keep,
        activeEmployerInfo,
      });
      return clip;
    } catch (error) {
      return rejectWithValue(toCameraApiError(error));
    }
  }
);

/** Deletes a clip (managers); resolves to its id. */
export const removeCameraClip = createAsyncThunk(
  'cameraRecordings/removeClip',
  async ({ projectId, cameraId, clipId }, { getState, rejectWithValue }) => {
    try {
      const activeEmployerInfo = selectUserActiveEmployerInfo(getState());
      await deleteCameraClip({
        projectId,
        cameraId,
        clipId,
        activeEmployerInfo,
      });
      return clipId;
    } catch (error) {
      return rejectWithValue(toCameraApiError(error));
    }
  }
);

/** Deletes a still (managers); resolves to its id. */
export const removeCameraStill = createAsyncThunk(
  'cameraRecordings/removeStill',
  async ({ projectId, cameraId, stillId }, { getState, rejectWithValue }) => {
    try {
      const activeEmployerInfo = selectUserActiveEmployerInfo(getState());
      await deleteCameraStill({
        projectId,
        cameraId,
        stillId,
        activeEmployerInfo,
      });
      return stillId;
    } catch (error) {
      return rejectWithValue(toCameraApiError(error));
    }
  }
);

/**
 * A clip's signed MP4 (`{ url, thumbnailUrl?, expiresAt }`), to play or,
 * with `download`, to save; rejects with `{ status, code, message }`. A
 * plain thunk that dispatches nothing: each URL is asked for when it is
 * needed (the API logs the view or the download), never kept in the store.
 */
export const fetchCameraClipUrl =
  ({ projectId, cameraId, clipId, download = false }) =>
  async (dispatch, getState) => {
    const activeEmployerInfo = selectUserActiveEmployerInfo(getState());
    try {
      return await getCameraClipUrl({
        projectId,
        cameraId,
        clipId,
        download,
        activeEmployerInfo,
      });
    } catch (error) {
      throw toCameraApiError(error);
    }
  };
